import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { resolve } from '../utils/resolve';
import { map, mergeMap } from 'rxjs/operators';
import * as _ from 'lodash';
import {
  AreasConhecimento,
  Candidato,
  Email,
  Favorito,
  Formacao,
  Idioma,
  Pais,
  Projeto,
  Telefone
} from '../interfaces/candidato';
import { Resposta } from 'src/app/interfaces/resposta';
import { storage } from 'src/app/utils/storage';
import { SituacaoEnum } from 'src/app/enums/situacao.enum';
import { QuestionarioItem } from '../interfaces/questionario';
import { limparStrings } from '../utils/utilitarias';

@Injectable({
  providedIn: 'root'
})
export class CandidatoService {

  public candidato$: ReplaySubject<any>;
  public candidatoExcluido$: ReplaySubject<boolean>;
  public foto$: ReplaySubject<string>;

  public favoritos$: ReplaySubject<[ Favorito ]> = new ReplaySubject<[ Favorito ]>();
  private candidatoLogado: Candidato;

  constructor(
    private http: HttpClient
  ) {
    this.candidato$ = new ReplaySubject(1);
    this.candidatoExcluido$ = new ReplaySubject(1);
    this.foto$ = new ReplaySubject(1);

    this.candidatoExcluido$.pipe(mergeMap((excluido, index) => {
      this.candidato$.next(undefined);
      return of(excluido);
    })).subscribe();
    this.candidato$.pipe(mergeMap(candidato => {
        console.log('candidato$ Atualizado!');
        this.candidatoLogado = candidato;
        return storage.set('candidato', candidato);
      }
    )).subscribe();
  }

  listar(filtros?: CandidatoFiltro): Observable<Resposta<Candidato[]>> {
    const filter = _.cloneDeep(filtros);
    if (filter && filter.paises) {
      filter.paisIds = [];
      filter.paises.forEach(
        (pais) => {
          filter.paisIds.push(pais.id);
        }
      );
      delete filter.paises;
    }
    if (filter && filter.areasConhecimento) {
      filter.areaConhecimentoIds = [];
      filter.areasConhecimento.forEach(
        (area) => {
          console.log('area', area);
          filter.areaConhecimentoIds.push(area.idAreaConhecimento);
        }
      );
      delete filter.areasConhecimento;
    }
    const url = resolve('egresso://candidatos', { ...filter });
    return this.http.get<Resposta<Candidato[]>>(url);
  }

  async atualizaCandidatoLogado() {
    if (!this.candidatoLogado || !this.candidatoLogado._id) {
      storage.get('candidato').then(c => {
        if (c) {
          const url = resolve(
            'egresso://candidatos/selecionar',
            { id: c._id }
          );
          return this.http.get<Resposta<Candidato>>(url).pipe(
            map((resposta) => {
              this.usar(resposta.data);
            })
          ).subscribe();
        }
      });
    }

  }

  candidatoId(id?: string): Observable<Candidato> {
    let idCandidato = '5fbbba507d0c763dfbede779';
    if (id) idCandidato = id;
    const url = resolve(
      'egresso://candidatos/selecionar',
      { id: idCandidato }
    );
    return this.http.get<Resposta<Candidato>>(url).pipe(
      map((resposta) => resposta.data)
    );
  }

  cadastrarIdioma(idCandidato: string, idioma: Idioma) {
    idioma = limparStrings(idioma);
    const url = resolve('egresso://candidatos/idioma', { id: idCandidato });
    return this.http.post<Resposta<Idioma>>(url, idioma).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarIdioma(id: string, idioma: Idioma) {
    idioma = limparStrings(idioma);
    const url = resolve('egresso://candidatos/idioma', { id });
    return this.http.put<Resposta<Idioma>>(url, idioma).pipe(
      map((resposta) => resposta.data)
    );
  }

  salvarDados(atuacao: Candidato): Observable<Candidato> {
    atuacao = limparStrings(atuacao);
    const url = resolve('rnc://salvar-dados-atuacao');
    return this.http.post<Candidato>(url, atuacao);
  }

  salvarCandidato(candidato: Candidato): Observable<Candidato> {
    candidato = limparStrings(candidato);
    const url = resolve('egresso://candidatos');
    return this.http.post<Resposta<Candidato>>(url, candidato).pipe(
      map((resposta) => resposta.data)
    );
  }

  alteraCandidato(id: string, candidato: Candidato): Observable<Candidato> {
    candidato = limparStrings(candidato);
    const url = resolve('egresso://candidato/id', { id });
    return this.http.put<Resposta<Candidato>>(url, candidato).pipe(
      map((resposta) => resposta.data)
    );
  }

  cadastrarFormacao(idCandidato: string, formacao: Formacao) {
    formacao = limparStrings(formacao);
    const url = resolve('egresso://candidatos/formacao', { id: idCandidato });
    return this.http.post<Resposta<Formacao>>(url, formacao).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarFormacao(id: string, formacao: Formacao) {
    formacao = limparStrings(formacao);
    const url = resolve('egresso://candidatos/formacao', { id });
    return this.http.put<Resposta<Formacao>>(url, formacao).pipe(
      map((resposta) => resposta.data)
    );
  }

  cadastrarProjeto(projeto: Projeto) {
    projeto = limparStrings(projeto);
    const id = this.candidatoLogado._id;
    const url = resolve('egresso://candidatos/projeto', { id: id });
    return this.http.post<Resposta<Projeto>>(url, projeto).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarProjeto(projeto: Projeto) {
    projeto = limparStrings(projeto);
    const id = this.candidatoLogado._id;
    const url = resolve('egresso://candidatos/projeto', { id });
    return this.http.put<Resposta<Projeto>>(url, projeto).pipe(
      map((resposta) => resposta.data)
    );
  }

  cadastrarTelefone(idCandidato: string, telefone: Telefone) {
    telefone = limparStrings(telefone);
    const url = resolve('egresso://candidatos/telefone', { id: idCandidato });
    return this.http.post<Resposta<Telefone>>(url, telefone).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarTelefone(id: string, telefone: Telefone) {
    telefone = limparStrings(telefone);
    const url = resolve('egresso://candidatos/telefone', { id });
    return this.http.put<Resposta<Telefone>>(url, telefone).pipe(
      map((resposta) => resposta.data)
    );
  }

  cadastrarEmail(idCandidato: string, email: Email) {
    email = limparStrings(email);
    const url = resolve('egresso://candidatos/email', { id: idCandidato });
    return this.http.post<Resposta<Email>>(url, email).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarEmail(id: string, email: Email) {
    email = limparStrings(email);
    const url = resolve('egresso://candidatos/email', { id });
    return this.http.put<Resposta<Email>>(url, email).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarAreasConhecimento(id: string, areas: AreasConhecimento) {
    areas = limparStrings(areas);
    const url = resolve('egresso://candidatos/areas', { id });
    return this.http.put<Resposta<AreasConhecimento>>(url, areas).pipe(
      map((resposta) => resposta.data)
    );
  }

  alterarQuestionario(questoes: QuestionarioItem[]) {
    questoes = limparStrings(questoes);
    const id = this.candidatoLogado._id;
    const url = resolve('egresso://candidatos/questionario', { id });
    return this.http.put<Resposta<QuestionarioItem[]>>(url, questoes).pipe(
      map((resposta) => {
        this.candidatoLogado.questionario = resposta.data;
        this.usar(this.candidatoLogado);
        return resposta.data;
      })
    );
  }

  alterarFavorito(favorito: Favorito): Observable<any> {
    if (!this.candidatoLogado || !this.candidatoLogado._id) {
      return this.favoritosLocal(favorito);
    }

    const id = this.candidatoLogado._id;
    const url = resolve('egresso://candidatos/favorito', { id });

    console.log('Altera favoritos');

    return this.http.put<Resposta<[ Favorito ]>>(url, favorito).pipe(
      map((resposta) => {
        this.candidatoLogado.favoritos = resposta.data;
        storage.set('favoritos', resposta.data);
        this.usar(this.candidatoLogado);
        return resposta.data;
      })
    );
  }

  getFavorito() {
    return storage.get('favoritos');
  }

  favoritosSincronizar() {
    // vamos pegar todos os favoritos locais e atualizar no servidor
    try {
      const idCandidato = this.candidatoLogado._id;
      storage.get('favoritos').then((favoLocal) => {
        console.log(favoLocal);
        const url = resolve('egresso://candidatos/favorito', { id: idCandidato });
        this.http.post<Resposta<[ Favorito ]>>(url, favoLocal).pipe(
          map((resposta) => {
            this.candidatoLogado.favoritos = resposta.data;
            storage.set('favoritos', resposta.data);
            this.usar(this.candidatoLogado);
            return resposta.data;
          })).subscribe();
      });
    } catch (e) {
      console.log(e);
    }
  }

  private favoritosLocal(favorito: Favorito) {
    // Favoritos será salvo tmb localmente para controlar a lista caso o usuário não estejá logado
    return new Observable((observer) => {
      console.log('USUário não está logado');
      storage.get('favoritos').then((favoLocal) => {
        console.log('favoLocal', favoLocal);
        if (favoLocal) {
          const index = favoLocal.findIndex(e => e.id === favorito.id);
          if (index >= 0) {
            favoLocal.splice(index, 1);
          } else {
            favoLocal.push(favorito);
          }
        } else {
          favoLocal = [];
          favoLocal.push(favorito);
        }
        storage.set('favoritos', favoLocal);
        this.favoritos$.next(favoLocal);
        observer.next(favoLocal);
      });
    });
  }

  uploadImagem(id: string, imagem: any) {
    const url = resolve('egresso://candidatos/upload', { id });
    return this.http.post<Resposta<any>>(url, imagem).pipe(
      map((resposta) => {
        this.foto$.next();
        return resposta.data;
      })
    );
  }

  usar(candidato: Candidato): Observable<Candidato> {
    console.log('Candidato Atualizado!');
    this.candidatoLogado = candidato;
    this.candidato$.next(candidato);
    return of(candidato);
  }

  candidato(): Promise<Candidato> {
    return storage.get('candidato');
  }

  getImagemPerfil(filename: string): string {
    return resolve('egresso://candidatos/render', { filename });
  }

  apagarPerfil(id: string) {
    const url = resolve('egresso://candidatos/perfil', { id });
    return this.http.delete<Resposta<AreasConhecimento>>(url).pipe(
      map((resposta) => resposta.data)
    );
  }
}

export interface CandidatoFiltro {
  cpf?: string;
  situacao?: SituacaoEnum;
  tituloIds?: Array<number>;
  aplicacaoIds?: Array<number>;
  areasConhecimento?: Array<AreasConhecimento>;
  areaConhecimentoIds?: Array<number>;
  paisIds?: Array<number>;
  paises?: Array<Pais>;
  ultimaFormacao?: boolean;
  execAtividade?: boolean;
  qtFormacoes?: number;
  qtArtigos?: number;
  nome?: string;
  ids?: Array<string>;
  tags?: Array<string>;
  page?: number;
  count?: number;
}
